import { consola } from 'consola'
import { ref } from 'vue'
import { useInstitutionStore } from '@entities/institution'

/**
 * Типы устройств для определения платформы
 */
export type DeviceType = 'ios' | 'android' | 'desktop' | 'unknown'

/**
 * Результат определения устройства
 */
export interface DeviceInfo {
  type: DeviceType
  userAgent: string
}

/**
 * Composable для управления скачиванием приложения с автоматическим редиректом
 *
 * Возможности:
 * - Определение типа устройства пользователя
 * - Автоматический редирект на соответствующий маркетплейс
 * - Fallback на показ кнопок если редирект не сработал
 * - Получение ссылок на маркетплейсы из API
 */
export const useAppDownloadRedirect = () => {
  const storeInstitution = useInstitutionStore()

  const isRedirecting = ref(false)
  const showButtons = ref(false)
  const deviceInfo = ref<DeviceInfo>({
    type: 'unknown',
    userAgent: '',
  })

  /**
   * Определяет тип устройства по User Agent
   */
  const detectDevice = (): DeviceInfo => {
    if (!process.client) {
      return { type: 'unknown', userAgent: '' }
    }

    const userAgent = navigator.userAgent || navigator.vendor || ''
    const ua = userAgent.toLowerCase()

    // Проверяем iOS устройства
    if (/iphone|ipad|ipod/.test(ua)) {
      return { type: 'ios', userAgent }
    }

    // Проверяем Android устройства
    if (/android/.test(ua)) {
      return { type: 'android', userAgent }
    }

    // Проверяем является ли устройство мобильным
    const isMobile = /mobile|tablet|ip(ad|hone|od)|android|silk|webos|blackberry|iemobile|opera mini/i.test(ua)

    if (isMobile) {
      // Если определили как мобильное, но не iOS и не Android
      return { type: 'unknown', userAgent }
    }

    // Desktop устройство
    return { type: 'desktop', userAgent }
  }

  /**
   * Получает ссылки на маркетплейсы из данных institution
   */
  const getMarketplaceUrls = () => {
    const institution = storeInstitution.institution

    consola.debug('[useAppDownloadRedirect] 🔍 Получаем ссылки:', {
      hasInstitution: !!institution,
      institutionId: institution?.id,
      googlePlayUrl: institution?.google_play_url,
      appStoreUrl: institution?.app_store_url,
    })

    return {
      android: institution?.google_play_url || null,
      ios: institution?.app_store_url || null,
    }
  }

  /**
   * Выполняет автоматический редирект на соответствующий маркетплейс
   * Возвращает true если редирект был выполнен, false если нет
   */
  const attemptAutoRedirect = async (device: DeviceType): Promise<boolean> => {
    if (!process.client) return false

    const urls = getMarketplaceUrls()

    let targetUrl: string | null = null

    switch (device) {
      case 'ios': {
        targetUrl = urls.ios
        break
      }
      case 'android': {
        targetUrl = urls.android
        break
      }
      default: {
        // Для desktop и unknown не делаем автоматический редирект
        return false
      }
    }

    if (!targetUrl) {
      consola.warn(`[useAppDownloadRedirect] Нет URL для платформы: ${device}`)
      return false
    }

    try {
      consola.debug(`[useAppDownloadRedirect] Попытка редиректа на: ${targetUrl}`)
      isRedirecting.value = true

      // Пытаемся открыть ссылку
      window.location.href = targetUrl

      // Даем время на редирект (если сработало, страница закроется/перейдет)
      await new Promise(resolve => setTimeout(resolve, 2000))

      // Если через 2 секунды мы все еще здесь, значит редирект не сработал
      // (например, приложение не установлено)
      return false
    } catch (error) {
      consola.error('[useAppDownloadRedirect] Ошибка при попытке редиректа:', error)
      return false
    } finally {
      isRedirecting.value = false
    }
  }

  /**
   * Выполняет ручной переход на маркетплейс
   * Используется при клике на кнопки
   */
  const redirectToMarketplace = (platform: 'ios' | 'android') => {
    if (!process.client) return

    const urls = getMarketplaceUrls()
    const targetUrl = platform === 'ios' ? urls.ios : urls.android

    if (!targetUrl) {
      consola.warn(`[useAppDownloadRedirect] Нет URL для платформы: ${platform}`)
      return
    }

    consola.debug(`[useAppDownloadRedirect] Ручной переход на: ${targetUrl}`)
    window.open(targetUrl, '_blank')
  }

  /**
   * Инициализирует автоматический редирект при монтировании компонента
   * Если редирект не сработал, показывает кнопки
   */
  const initAutoRedirect = async () => {
    if (!process.client) return

    // Определяем устройство
    deviceInfo.value = detectDevice()
    consola.debug('[useAppDownloadRedirect] Определено устройство:', deviceInfo.value)

    // Для desktop сразу показываем кнопки
    if (deviceInfo.value.type === 'desktop' || deviceInfo.value.type === 'unknown') {
      showButtons.value = true
      return
    }

    // Пытаемся сделать автоматический редирект для мобильных устройств
    const redirected = await attemptAutoRedirect(deviceInfo.value.type)

    // Если редирект не сработал, показываем кнопки
    if (!redirected) {
      consola.debug('[useAppDownloadRedirect] Автоматический редирект не удался, показываем кнопки')
      showButtons.value = true
    }
  }

  return {
    // Состояние
    isRedirecting,
    showButtons,
    deviceInfo,

    // Методы
    detectDevice,
    getMarketplaceUrls,
    attemptAutoRedirect,
    redirectToMarketplace,
    initAutoRedirect,
  }
}
